/**
 * OUW-88 — links to contact the agent from the Client Portal.
 *
 * Product exposes WhatsApp and mailto when GET brings phone and/or email.
 * Missing / invalid data → null (caller hides the CTA).
 */

import { firstNameFrom } from './portalService';

export type AgentContact = {
  name: string;
  phone: string;
  email: string;
};

export type AgentContactLinks = {
  talkName: string;
  wa: string | null;
  mail: string | null;
};

/** Digits only for wa.me. Strips a leading international `00` prefix. */
export function agentPhoneDigits(phone: string): string {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  return digits;
}

/**
 * WhatsApp deep link. Requires 8–15 digits (E.164 without '+').
 * Invalid length → null so the CTA is not drawn.
 */
export function agentWhatsAppHref(phone: string, text = ''): string | null {
  const digits = agentPhoneDigits(phone);
  if (digits.length < 8 || digits.length > 15) return null;
  const q = text ? `?text=${encodeURIComponent(text)}` : '';
  return `https://wa.me/${digits}${q}`;
}

export function agentMailtoHref(email: string, subject = ''): string | null {
  const addr = email.trim();
  if (!addr || !addr.includes('@')) return null;
  const q = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return `mailto:${addr}${q}`;
}

/** Single derivation: talk name + WA/mail hrefs for a reference. */
export function agentContactLinks(
  agent: Partial<AgentContact> | null | undefined,
  reference: string,
): AgentContactLinks {
  const name = (agent?.name || '').trim();
  return {
    talkName: firstNameFrom(name),
    wa: agentWhatsAppHref(agent?.phone || '', reference),
    mail: agentMailtoHref(agent?.email || '', reference),
  };
}
